import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Plus, Minus, Trash2, RotateCcw } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useUserStore, selectIsSessionValid } from '../store/userStore';
import { operatingStatusQueryOptions } from '../queries/operatingStatus';
import { loyaltyPolicyQueryOptions } from '../queries/loyaltyPolicy';
import type { CartItem } from '../context/CartContext';
import { appPath, appLoginPath } from '../routes/appPaths';
import { applyGuestDiscount } from '../loyalty/guestDiscount';
function CartItemCard({
  item,
  onRemove,
  onIncrement,
  onDecrement,
}: {
  item: CartItem;
  onRemove: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
}) {
  return (
    <div
      className="rounded-3xl overflow-hidden"
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <div className="flex items-center gap-3 p-3">
        {item.image ? (
          <div className="w-16 h-16 rounded-2xl overflow-hidden shrink-0" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <img src={item.image} alt="" className="w-full h-full object-cover" />
          </div>
        ) : null}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm leading-tight truncate" style={{ color: '#fff' }}>
            {item.name}
          </p>
          <p className="font-bold text-base mt-1" style={{ color: '#fff' }}>
            {item.price * item.quantity} ₽
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            onClick={onRemove}
            className="w-7 h-7 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.08)' }}
          >
            <Trash2 size={13} style={{ color: 'rgba(255,255,255,0.4)' }} />
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onDecrement}
              className="w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90"
              style={{ background: 'rgba(255,255,255,0.1)', color: '#fff' }}
            >
              <Minus size={13} />
            </button>
            <span className="text-sm font-bold w-5 text-center" style={{ color: '#fff' }}>
              {item.quantity}
            </span>
            <button
              onClick={onIncrement}
              className="w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90"
              style={{ background: 'rgba(255,255,255,0.1)', color: '#fff' }}
            >
              <Plus size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PendingRemovalCard({
  item,
  onRestore,
  onExpire,
}: {
  item: CartItem;
  onRestore: () => void;
  onExpire: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onExpire, 5000);
    return () => clearTimeout(timer);
  }, [item.id, onExpire]);

  return (
    <div
      className="rounded-3xl overflow-hidden"
      style={{
        background: 'rgba(255,255,255,0.5)',
        border: '1px solid rgba(0,0,0,0.06)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}
    >
      <div className="flex items-center gap-3 p-3 opacity-60">
        <div className="w-16 h-16 rounded-2xl overflow-hidden shrink-0 bg-black/5">
          {item.image ? (
            <img src={item.image} alt="" className="w-full h-full object-cover opacity-60" />
          ) : (
            <div className="w-full h-full bg-black/10" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm leading-tight truncate" style={{ color: '#666' }}>
            {item.name}
          </p>
          <p className="font-bold text-base mt-1" style={{ color: '#888' }}>
            {item.price * item.quantity} ₽
          </p>
        </div>
        <button
          onClick={onRestore}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl font-semibold text-sm transition-all active:scale-95"
          style={{
            background: '#0a0a0a',
            color: '#fff',
          }}
        >
          <RotateCcw size={14} />
          Вернуть
        </button>
      </div>
    </div>
  );
}

export default function CartScreen() {
  const navigate = useNavigate();
  const isAuthed = useUserStore(selectIsSessionValid);
  const {
    items,
    pendingRemovals,
    total,
    increment,
    decrement,
    remove,
    restore,
    confirmRemoval,
    clear,
    deliveryMode,
  } = useCart();
  const { data: operatingStatus } = useQuery(operatingStatusQueryOptions());
  const { data: loyaltyPolicy } = useQuery(loyaltyPolicyQueryOptions());
  const scheduleClosed = operatingStatus && !operatingStatus.isOpen;
  const pickupDisabled = deliveryMode === 'pickup';
  const deliveryDisabled = deliveryMode === 'delivery';
  const discountPercent = isAuthed ? (loyaltyPolicy?.discountPercent ?? 0) : 0;
  const pricing = applyGuestDiscount(total, discountPercent);

  function handleCheckout() {
    if (pickupDisabled || deliveryDisabled) return;
    if (!isAuthed) {
      navigate(appLoginPath(appPath('/checkout')));
      return;
    }
    navigate(appPath('/checkout'));
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0a0a0a' }}>
      <div className="max-w-md mx-auto w-full flex flex-col flex-1">

        {/* Header */}
        <div className="flex items-center gap-3 px-4 pt-5 pb-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-2xl flex items-center justify-center transition-all active:scale-90"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <ArrowLeft size={20} style={{ color: '#fff' }} />
          </button>
          <h1
            className="text-xl font-bold uppercase"
            style={{ color: '#fff', letterSpacing: '-0.02em', fontFamily: 'Unbounded, sans-serif' }}
          >
            Корзина
          </h1>
          {(items.length > 0 || pendingRemovals.length > 0) && (
            <button
              onClick={clear}
              className="ml-auto text-xs font-medium transition-colors"
              style={{ color: '#aaa' }}
            >
              Очистить
            </button>
          )}
        </div>

        {items.length === 0 && pendingRemovals.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center pb-28 px-8 text-center">
            <div className="text-6xl mb-4">🛒</div>
            <p className="font-semibold text-lg mb-1" style={{ color: '#fff' }}>
              Корзина пуста
            </p>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Добавьте блюда из меню
            </p>
            <button
              onClick={() => navigate(appPath('/home'))}
              className="px-6 py-3 rounded-2xl font-semibold text-sm tracking-[0.1em] uppercase"
              style={{
                background: '#fff',
                color: '#0a0a0a',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              Перейти в меню
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 px-4 pb-4 space-y-3 overflow-y-auto">
              {items.map(item => (
                <CartItemCard
                  key={item.id}
                  item={item}
                  onRemove={() => remove(item.id)}
                  onIncrement={() => increment(item.id)}
                  onDecrement={() => decrement(item.id)}
                />
              ))}
              {pendingRemovals.map(item => (
                <PendingRemovalCard
                  key={item.id}
                  item={item}
                  onRestore={() => restore(item.id)}
                  onExpire={() => confirmRemoval(item.id)}
                />
              ))}
            </div>

            {/* Summary */}
            <div className="px-4 pb-24 pt-2">
              <div
                className="rounded-3xl p-4 mb-3"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <div className="flex justify-between text-sm mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  <span>Блюда</span>
                  <span>{pricing.subtotal} ₽</span>
                </div>
                {pricing.discountSum > 0 && (
                  <div className="flex justify-between text-sm mb-2" style={{ color: '#e8c872' }}>
                    <span>Скидка {pricing.discountPercent}%</span>
                    <span>−{pricing.discountSum} ₽</span>
                  </div>
                )}
                <div className="flex justify-between text-sm mb-3" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  <span>Доставка</span>
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>Бесплатно</span>
                </div>
                <div
                  className="flex justify-between font-bold text-lg pt-3"
                  style={{ borderTop: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
                >
                  <span>Итого</span>
                  <span>{pricing.payable} ₽</span>
                </div>
              </div>

              {(pickupDisabled || deliveryDisabled) && (
                <p
                  className="text-center text-xs mb-3 px-2 leading-relaxed"
                  style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Manrope, sans-serif' }}
                >
                  {deliveryDisabled
                    ? 'Заказы на доставку временно не принимаются'
                    : 'Заказы на самовывоз временно не принимаются'}
                </p>
              )}

              <button
                onClick={handleCheckout}
                disabled={items.length === 0 || !!scheduleClosed || pickupDisabled || deliveryDisabled}
                className="w-full py-4 rounded-2xl font-bold text-base transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed tracking-[0.08em] uppercase text-sm"
                style={{
                  background: '#fff',
                  color: '#0a0a0a',
                  fontFamily: 'Manrope, sans-serif',
                }}
              >
                {scheduleClosed
                  ? 'Кафе закрыто'
                  : isAuthed
                    ? 'Оформить заказ'
                    : 'Войти для оформления'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
